import { z } from 'zod';
// Group-level roles (used in GroupMember schema)
export const groupMemberRoleValues = ['organizer', 'member'];
export const groupMemberRoleSchema = z.enum(groupMemberRoleValues);
export const memberStatusValues = ['pending', 'active'];
export const memberStatusSchema = z.enum(memberStatusValues);
export const meetingStatusValues = ['scheduled', 'canceled'];
export const meetingStatusSchema = z.enum(meetingStatusValues);
export const eventTypeValues = ['in-person', 'online', 'hybrid'];
export const eventTypeSchema = z.enum(eventTypeValues);
export const messageScopeValues = ['group', 'meeting'];
export const messageScopeSchema = z.enum(messageScopeValues);
export const isoDateTimeSchema = z
    .string()
    .datetime({ message: 'Expected an ISO 8601 timestamp' });
export const themeValues = ['light', 'dark', 'system'];
export const themeSchema = z.enum(themeValues);
export const userPreferencesSchema = z.object({
    emailOptIn: z.boolean().default(true),
    pushNotificationOptIn: z.boolean().default(true),
    theme: themeSchema.default('system'),
    notificationToken: z.string().optional(),
    updatedAt: isoDateTimeSchema.optional()
});
export const accountStatusValues = ['active', 'pending', 'suspended'];
export const accountStatusSchema = z.enum(accountStatusValues);
export const registrationMethodValues = [
    'open',
    'invite',
    'approval',
    'oauth',
    'oauth-invite',
    'oauth-pending-invite',
    'oauth-pending-approval'
];
export const registrationMethodSchema = z.enum(registrationMethodValues);
export const userSchema = z.object({
    uid: z.string().min(1),
    displayName: z.string().min(1),
    photoURL: z.string().url().optional(),
    email: z.string().email(),
    isAdmin: z.boolean().default(false), // Platform admin flag
    accountStatus: accountStatusSchema.default('active'), // Account registration status
    registrationMethod: registrationMethodSchema.optional(),
    createdAt: isoDateTimeSchema.optional(),
    preferences: userPreferencesSchema.optional()
});
// Group approval status values
export const groupApprovalStatusValues = ['approved', 'pending', 'rejected'];
export const groupApprovalStatusSchema = z.enum(groupApprovalStatusValues);
export const groupSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(80),
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9-]+$/, {
        message: 'Slug must include lowercase letters, numbers, or hyphen only'
    }),
    organizerUid: z.string().min(1),
    organizerName: z.string().min(1).default('Unknown Organizer'), // Denormalized organizer display name
    createdAt: isoDateTimeSchema,
    autoAcceptMembers: z.boolean(),
    memberCount: z.number().int().min(0).optional(),
    summary: z.string().max(280).optional(),
    // Group approval workflow fields
    approvalStatus: groupApprovalStatusSchema.default('approved'),
    approvedBy: z.string().min(1).optional(),
    approvedAt: isoDateTimeSchema.optional(),
    // Media/Asset fields
    posterUrl: z.string().url().optional(),
    posterAlt: z.string().max(200).optional(),
    posterStoragePath: z.string().optional(),
});
export const groupWriteSchema = groupSchema
    .omit({ id: true, createdAt: true })
    .extend({
    createdAt: isoDateTimeSchema.optional(),
    approvalStatus: groupApprovalStatusSchema.optional(), // Make optional for form input
});
export const groupMemberSchema = z.object({
    uid: z.string().min(1),
    role: groupMemberRoleSchema, // Group-level role: 'organizer' | 'member'
    status: memberStatusSchema,
    joinedAt: isoDateTimeSchema,
    // Denormalized user profile data - stored when user joins group
    displayName: z.string().min(1).default('Unknown Member'),
    email: z.string().email().default('unknown@example.com'),
    photoURL: z.string().url().optional(),
});
// Agenda item schema for meeting agenda
export const agendaItemSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1).max(150),
    description: z.string().max(500).optional(),
    duration: z.number().int().positive().optional(), // duration in minutes
    presenter: z.string().max(100).optional(),
    order: z.number().int().min(0)
});
// Co-host schema for meeting co-hosts
export const coHostSchema = z.object({
    uid: z.string().min(1),
    displayName: z.string().min(1),
    role: z.string().max(50).optional() // e.g., "Co-organizer", "Speaker", "Facilitator"
});
export const meetingSchema = z.object({
    id: z.string().min(1),
    groupId: z.string().min(1),
    title: z.string().min(1).max(120),
    description: z.string().max(2000),
    startsAt: isoDateTimeSchema,
    endsAt: isoDateTimeSchema,
    eventType: eventTypeSchema,
    location: z
        .object({
        label: z.string().min(1).max(120),
        address: z.string().max(280).optional(),
        mapUrl: z.string().url().optional() // Google Maps or similar link
    })
        .optional(),
    onlineUrl: z.string().url().optional(),
    capacity: z.number().int().positive().optional(),
    attendeeCount: z.number().int().min(0).default(0),
    status: meetingStatusSchema,
    createdBy: z.string().min(1),
    createdAt: isoDateTimeSchema,
    // Media/Asset fields
    posterUrl: z.string().url().optional(),
    posterAlt: z.string().max(200).optional(),
    posterStoragePath: z.string().optional(),
    // Enhanced event fields
    agenda: z.array(agendaItemSchema).optional(),
    coHosts: z.array(coHostSchema).optional(),
    tags: z.array(z.string().min(1).max(30)).max(10).optional(), // event tags/categories
    requiresApproval: z.boolean().default(false) // for RSVP approval workflow
});
export const meetingWriteSchema = meetingSchema
    .omit({ id: true, createdAt: true, attendeeCount: true })
    .extend({
    attendeeCount: z.number().int().min(0).optional(),
    createdAt: isoDateTimeSchema.optional()
});
export const subscriptionSchema = z.object({
    id: z.string().min(1),
    uid: z.string().min(1),
    groupId: z.string().min(1).optional(),
    meetingId: z.string().min(1).optional(),
    token: z.string().min(1),
    createdAt: isoDateTimeSchema
});
// RSVP status values
export const rsvpStatusValues = ['attending', 'not_attending'];
export const rsvpStatusSchema = z.enum(rsvpStatusValues);
// RSVP approval status values
export const rsvpApprovalStatusValues = ['pending', 'approved', 'rejected'];
export const rsvpApprovalStatusSchema = z.enum(rsvpApprovalStatusValues);
// RSVP schema with approval workflow support
export const rsvpSchema = z.object({
    id: z.string().min(1),
    meetingId: z.string().min(1),
    userId: z.string().min(1),
    status: rsvpStatusSchema, // 'attending' | 'not_attending'
    // Approval workflow fields
    approvalStatus: rsvpApprovalStatusSchema.default('approved'), // 'pending' | 'approved' | 'rejected'
    approvedBy: z.string().min(1).optional(), // UID of organizer who approved/rejected
    approvedAt: isoDateTimeSchema.optional(), // Timestamp of approval/rejection
    rejectionReason: z.string().max(500).optional(), // Optional reason for rejection
    // Metadata
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    // Denormalized user data (for organizer UI)
    userDisplayName: z.string().min(1).default('Unknown User'),
    userEmail: z.string().email().optional(),
    userPhotoURL: z.string().url().optional(),
});
// Write schema (for creating/updating RSVPs)
export const rsvpWriteSchema = rsvpSchema
    .omit({ id: true, createdAt: true, updatedAt: true })
    .extend({
    createdAt: isoDateTimeSchema.optional(),
    updatedAt: isoDateTimeSchema.optional(),
});
export const messageSchema = z.object({
    id: z.string().min(1),
    scope: messageScopeSchema,
    groupId: z.string().min(1),
    meetingId: z.string().min(1).optional(),
    text: z.string().min(1).max(2000),
    createdBy: z.string().min(1),
    createdAt: isoDateTimeSchema
});
export const accountDeletionRequestSchema = z.object({
    id: z.string().min(1),
    uid: z.string().min(1),
    email: z.string().email(),
    displayName: z.string().min(1),
    reason: z.string().max(1000).optional(),
    requestedAt: isoDateTimeSchema,
    status: z.enum(['pending', 'processing', 'completed']).default('pending')
});
export const assertUserProfile = (input) => userSchema.parse(input);
export const assertUserPreferences = (input) => userPreferencesSchema.parse(input);
export const assertAccountDeletionRequest = (input) => accountDeletionRequestSchema.parse(input);
export const assertGroup = (input) => groupSchema.parse(input);
export const assertGroupMember = (input) => groupMemberSchema.parse(input);
export const assertMeeting = (input) => meetingSchema.parse(input);
export const assertSubscription = (input) => subscriptionSchema.parse(input);
export const assertMessage = (input) => messageSchema.parse(input);
export const createSlug = (input) => input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
export const nowIsoString = () => new Date().toISOString();
export const ensureChronology = (startIso, endIso) => {
    const start = Date.parse(startIso);
    const end = Date.parse(endIso);
    if (Number.isNaN(start) || Number.isNaN(end)) {
        throw new Error('Invalid ISO date provided');
    }
    if (start >= end) {
        throw new Error('Start time must be before end time');
    }
};
/**
 * Validate meeting data based on event type
 * - in-person: requires location
 * - online: requires onlineUrl
 * - hybrid: requires both
 */
export const validateMeetingByType = (meeting) => {
    if (!meeting.eventType) {
        throw new Error('Event type is required');
    }
    switch (meeting.eventType) {
        case 'in-person':
            if (!meeting.location?.label || meeting.location.label.trim() === '') {
                throw new Error('In-person events must have a location');
            }
            break;
        case 'online':
            if (!meeting.onlineUrl || meeting.onlineUrl.trim() === '') {
                throw new Error('Online events must have a meeting URL');
            }
            break;
        case 'hybrid':
            if (!meeting.location?.label || meeting.location.label.trim() === '' || !meeting.onlineUrl || meeting.onlineUrl.trim() === '') {
                throw new Error('Hybrid events must have both location and online URL');
            }
            break;
    }
};
// ================================
// Canonical Organizer Configuration
// ================================
/**
 * Canonical organizer allowlist - these are the only accounts authorized to create
 * groups, meetings, broadcasts, and upload assets for the Penticton.dev platform.
 *
 * This enforces the single-organizer governance model where only Penticton.dev
 * team members can curate content across all groups.
 *
 * Environment variables override for testing:
 * - CANONICAL_ORGANIZER_UIDS: comma-separated UIDs
 * - CANONICAL_ORGANIZER_EMAILS: comma-separated emails
 */
// Production canonical organizers (Penticton.dev team)
const PRODUCTION_ORGANIZER_UIDS = [
    'mxFnv3IgStWEWGEKEtr4WvlfEL03',
    'tCVCpKKEggQaWqiG2PO5M6qOJnM2'
];
const PRODUCTION_ORGANIZER_EMAILS = [
    'info@penticton.dev',
    'info@penticton.dev'
];
/**
 * Get canonical organizer UIDs - supports environment override for testing
 */
export const getCanonicalOrganizerUids = () => {
    // Allow environment override for testing
    if (typeof process !== 'undefined' && process.env?.CANONICAL_ORGANIZER_UIDS) {
        return process.env.CANONICAL_ORGANIZER_UIDS.split(',').map(uid => uid.trim()).filter(Boolean);
    }
    return PRODUCTION_ORGANIZER_UIDS;
};
/**
 * Get canonical organizer emails - supports environment override for testing
 */
export const getCanonicalOrganizerEmails = () => {
    // Allow environment override for testing
    if (typeof process !== 'undefined' && process.env?.CANONICAL_ORGANIZER_EMAILS) {
        return process.env.CANONICAL_ORGANIZER_EMAILS.split(',').map(email => email.trim()).filter(Boolean);
    }
    return PRODUCTION_ORGANIZER_EMAILS;
};
/**
 * Check if a UID is authorized as a canonical organizer
 */
export const isCanonicalOrganizerUid = (uid) => {
    return getCanonicalOrganizerUids().includes(uid);
};
/**
 * Check if an email is authorized as a canonical organizer
 */
export const isCanonicalOrganizerEmail = (email) => {
    return getCanonicalOrganizerEmails().includes(email);
};
/**
 * Validate that a user has canonical organizer authorization
 * Throws descriptive error if not authorized
 */
export const validateCanonicalOrganizer = (uid, email) => {
    if (!uid && !email) {
        throw new Error('Authentication required - no user identity provided');
    }
    const isAuthorizedByUid = uid ? isCanonicalOrganizerUid(uid) : false;
    const isAuthorizedByEmail = email ? isCanonicalOrganizerEmail(email) : false;
    if (!isAuthorizedByUid && !isAuthorizedByEmail) {
        throw new Error(`Unauthorized: Only Penticton.dev organizers can perform this action. ` +
            `This platform is managed by the Penticton.dev team to curate quality tech events.`);
    }
};
export const createSecurityEvent = (action, uid, email, authorized = false, metadata) => ({
    action,
    uid,
    email,
    authorized,
    timestamp: nowIsoString(),
    ...metadata
});
// Re-export brand configuration
export * from './brand.js';
// Re-export permission types and utilities (after all schemas are defined)
export * from './permissions.js';
